import classNames from 'classnames';
import * as React from 'react';

import Icon from 'components/icon';
import { TempControlProductCard } from 'tempcontrol/hooks/use-tempcontrol-product-cards';
import { ResultProductSlot } from 'tempcontrol/utils/resolve-result-products';

interface ProductResultCardProps {
  headline: string;
  slot: ResultProductSlot;
  card?: TempControlProductCard | null;
  isLoading?: boolean;
  isSelected: boolean;
  onSelect?: () => void;
  /**
   * Total number of parallel (manifolded) valves the sizing calls for. 1 for a
   * single valve; when >1 the card makes the quantity explicit ("N × Model …")
   * so a manifolded recommendation reads as multiple valves, not one.
   */
  valveQuantity?: number;
  /**
   * Optional advisory box rendered inside the card body (e.g. the Hi-Lo note
   * shown only on the thermostatic/conventional card).
   */
  note?: React.ReactNode;
}

const MAX_FEATURES = 3;

// Theme `color(secondary)`. The cutup draws the selected-card accent as an
// inset border on `.c-card__body`, which excludes the note (a sibling of the
// body). We instead border the whole `<article>` so it wraps the body AND the
// note, and suppress the body's inset to avoid a double border.
const SELECTED_ACCENT = '#297fcb';

const isInteractiveTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return Boolean(target.closest('a, button'));
};

/**
 * Selectable valve result card matching tempcontrol-s5.php markup.
 * Selected state uses the featured (recommended) card; unselected uses
 * the compact alternate card with `u-flex@xl u-items-end` doc links.
 */
export const ProductResultCard: React.VFC<ProductResultCardProps> = ({
  headline,
  slot,
  card,
  isLoading = false,
  isSelected,
  onSelect,
  valveQuantity = 1,
  note,
}) => {
  const isSelectable = typeof onSelect === 'function';
  const isManifolded = valveQuantity > 1;
  const title = card?.name ?? slot.displayModel;
  const imageSrc = card?.featured_image;
  const description = card?.description;
  const features = (card?.features ?? []).slice(0, MAX_FEATURES);
  const specSheetUrl = card?.spec_sheet_url;
  const installationGuideUrl = card?.installation_guide_url;
  const productUrl = card?.permalink || null;

  const handleActivate = React.useCallback(() => {
    if (!isSelectable) return;
    onSelect();
  }, [isSelectable, onSelect]);

  const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isInteractiveTarget(event.target)) return;
    handleActivate();
  };

  const handleCardKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!isSelectable) return;
    // Let inner links/buttons (product page, spec/install docs) handle their
    // own Enter/Space so keyboard users can follow them instead of the press
    // being swallowed by the card's family-selection toggle.
    if (isInteractiveTarget(event.target)) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleActivate();
    }
  };

  const stopLinkPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const featureItems = features.map((feature) => (
    <li key={feature}>
      <Icon name="checkmark" className="o-svg-icon" />
      <p>{feature}</p>
    </li>
  ));

  return (
    <article
      className={classNames('c-card c-card--product c-card--product-featured', {
        'has-shadow c-card--active c-card--active-secondary u-mb-spacer-base-large': isSelected,
        'has-smaller-figure u-bg-neutral-100 u-mb-spacer-base-large': !isSelected,
      })}
      style={isSelected ? { border: `1px solid ${SELECTED_ACCENT}`, borderRadius: 0 } : undefined}
      role={isSelectable ? 'button' : undefined}
      tabIndex={isSelectable ? 0 : undefined}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div
        className={classNames(
          'c-card__body u-flex-row@sm u-flex-column@md u-flex-row@lg u-px-spacer-base',
          isSelected && 'u-px-gutter@xl u-pt-spacer-base-large@xl u-pb-spacer-base@xl'
        )}
        // The accent border now lives on the <article>; drop the cutup's inset
        // border on the body so the two don't stack.
        style={isSelected ? { boxShadow: 'none' } : undefined}
      >
        <figure
          className={classNames(
            'c-card__figure u-flex-shrink-0 u-items-start u-pr-spacer-base@sm u-pr-0@md',
            isSelected ? 'u-mr-gutter@lg' : 'u-pr-spacer-base@lg'
          )}
        >
          {imageSrc ? <img src={imageSrc} alt="" /> : <div className="c-card__figure-placeholder" />}
        </figure>
        <div className="c-card__content u-flex-grow@lg">
          <header className="c-card__header">
            <p className={classNames('c-headline', isSelected && 'u-text-secondary')}>{headline}</p>
            <h4 className={classNames('c-card__title', isSelected && 'card-title--large')}>
              {productUrl ? (
                <a href={productUrl} className="c-link-basic" target="_blank" rel="noreferrer">
                  {title}
                </a>
              ) : (
                title
              )}
            </h4>
            <p className="c-card__subtitle">
              {isManifolded ? `${valveQuantity} × Model ${slot.displayModel}` : `Model ${slot.displayModel}`}
            </p>
            {isManifolded && <p className="c-note u-mb-0">{valveQuantity} valves manifolded together in parallel</p>}

            {isLoading ? (
              <p className="c-note u-mt-spacer-base-small">Loading product details…</p>
            ) : isSelected ? (
              <>
                {(description || features.length > 0) && (
                  <div className="c-card__desc u-my-spacer-base u-text-xs">
                    {description && <p>{description}</p>}
                    {features.length > 0 && (
                      <ul className="c-compare__features c-compare__features--small">{featureItems}</ul>
                    )}
                  </div>
                )}
                {(specSheetUrl || installationGuideUrl) && (
                  <div className="c-list-grid" onClick={stopLinkPropagation}>
                    {specSheetUrl && (
                      <div className="u-mt-spacer-base-small">
                        <a href={specSheetUrl} className="c-button c-button--primary" target="_blank" rel="noreferrer">
                          <Icon name="download" className="o-svg-icon" />
                          <span>Specifications</span>
                        </a>
                      </div>
                    )}
                    {installationGuideUrl && (
                      <div className="u-mt-spacer-base-small">
                        <a href={installationGuideUrl} className="c-link-cta" target="_blank" rel="noreferrer">
                          <Icon name="download" className="o-svg-icon" />
                          <span>Installation</span>
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="u-flex@xl u-items-end u-justify-between@xl w-full@xl">
                <div>
                  {(description || features.length > 0) && (
                    <div className="c-card__desc u-text-xs">
                      {description && <p>{description}</p>}
                      {features.length > 0 && (
                        <ul className="c-compare__features c-compare__features--small u-mb-spacer">{featureItems}</ul>
                      )}
                    </div>
                  )}
                </div>
                {(specSheetUrl || installationGuideUrl) && (
                  <div className="u-flex-shrink-0@xl" onClick={stopLinkPropagation}>
                    {specSheetUrl && (
                      <div className="u-mb-spacer-base-small">
                        <a
                          href={specSheetUrl}
                          className="c-link-cta-light c-link-cta--small"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Icon name="download" className="o-svg-icon" />
                          <span>Specifications</span>
                        </a>
                      </div>
                    )}
                    {installationGuideUrl && (
                      <div className="u-mb-spacer-base-small">
                        <a
                          href={installationGuideUrl}
                          className="c-link-cta-light c-link-cta--small"
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Icon name="download" className="o-svg-icon" />
                          <span>Installation</span>
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </header>
        </div>
      </div>
      {note && (
        <div
          className={classNames(
            'u-px-spacer-base u-pt-spacer-base-small u-pb-spacer-base',
            isSelected && 'u-px-gutter@xl u-pb-spacer-base-large@xl'
          )}
        >
          {note}
        </div>
      )}
    </article>
  );
};

export default ProductResultCard;
